/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

type AccessibleLabel = { 'aria-label': string } | { 'aria-labelledby': string };

type BaseProps = {
  /** Controlled on/off state. */
  checked: boolean;
  /** Fired with the next state whenever the user toggles the switch. */
  onChange: (checked: boolean) => void;
  /** Renders the switch in a non-interactive, dimmed state. */
  disabled?: boolean;
  /** Test identifier forwarded to the underlying `<button>`. */
  'data-testid'?: string;
  /** Optional id forwarded to the underlying `<button>`. */
  id?: string;
};

export type ToggleSwitchProps = BaseProps & AccessibleLabel;

const TRACK_WIDTH = 36;
const TRACK_HEIGHT = 20;
const THUMB_SIZE = 16;
const THUMB_PADDING = (TRACK_HEIGHT - THUMB_SIZE) / 2;

const styles = {
  track: css`
    position: relative;
    display: inline-flex;
    align-items: center;
    width: ${TRACK_WIDTH}px;
    height: ${TRACK_HEIGHT}px;
    padding: 0;
    border: 0;
    border-radius: ${TRACK_HEIGHT / 2}px;
    background: var(--cds-color-neutral-stroke-primary);
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover:not(:disabled) {
      background: var(--cds-color-neutral-primary-weak);
    }

    &:focus-visible {
      outline: 2px solid var(--cds-color-interactive-stroke-primary-focus);
      outline-offset: 2px;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  `,
  trackChecked: css`
    background: var(--cds-color-interactive-primary);

    &:hover:not(:disabled) {
      background: var(--cds-color-interactive-primary-hover);
    }
  `,
  thumb: css`
    position: absolute;
    top: ${THUMB_PADDING}px;
    left: ${THUMB_PADDING}px;
    width: ${THUMB_SIZE}px;
    height: ${THUMB_SIZE}px;
    border-radius: 50%;
    background: var(--cds-color-neutral-background-primary);
    box-shadow: var(--cds-elevation-level1);
    transition: transform 0.15s ease;
  `,
  thumbChecked: css`
    transform: translateX(${TRACK_WIDTH - THUMB_SIZE - THUMB_PADDING * 2}px);
  `,
};

/**
 * Accessible on/off toggle switch.
 *
 * Renders a native `<button>` with `role="switch"` + `aria-checked`, so the
 * built-in keyboard handling (Space / Enter) and focus styles come for free.
 * Controlled only — pass `checked` + `onChange` and own the state upstream.
 *
 * @example
 * ```tsx
 * <ToggleSwitch
 *   checked={enabled}
 *   onChange={setEnabled}
 *   aria-label="Auto-send to new matches"
 * />
 * ```
 */
const ToggleSwitch = React.forwardRef<HTMLButtonElement, ToggleSwitchProps>(function ToggleSwitch(
  { checked, onChange, disabled, id, ...ariaProps },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={'aria-label' in ariaProps ? ariaProps['aria-label'] : undefined}
      aria-labelledby={'aria-labelledby' in ariaProps ? ariaProps['aria-labelledby'] : undefined}
      disabled={disabled}
      id={id}
      data-testid={ariaProps['data-testid']}
      onClick={() => onChange(!checked)}
      css={[styles.track, checked && styles.trackChecked]}
    >
      <span css={[styles.thumb, checked && styles.thumbChecked]} aria-hidden="true" />
    </button>
  );
});

export default ToggleSwitch;
